
const REMOTE_API = './data_handler.php';

export interface AiUsageEntry {
    id: string;
    model: string;
    inputTokens: number;
    outputTokens: number;
    timestamp: number;
    userName?: string;
    userEmail?: string;
    ipAddress?: string;
}

const STORAGE_KEY = 'gea_ndt_ai_usage';

export const logAiUsage = (model: string, inputTokens: number, outputTokens: number) => {
    try {
        const usage: AiUsageEntry[] = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
        const entry: AiUsageEntry = {
            id: crypto.randomUUID(),
            model,
            inputTokens,
            outputTokens,
            timestamp: Date.now(),
            userName: sessionStorage.getItem('nd_inspector_name') || 'Unknown',
            userEmail: sessionStorage.getItem('nd_inspector_email') || 'Unknown',
            ipAddress: sessionStorage.getItem('nd_inspector_ip') || 'Unknown'
        };
        usage.unshift(entry);
        // Keep last 500 entries
        localStorage.setItem(STORAGE_KEY, JSON.stringify(usage.slice(0, 500)));

        // Remote Synology Sync
        fetch(REMOTE_API, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ type: 'ai_usage', data: entry })
        }).catch(err => console.warn("Remote AI log failed:", err));
    } catch (e) {
        console.error("Failed to log AI usage:", e);
    }
};

export const clearAiUsageLogs = () => {
    localStorage.removeItem(STORAGE_KEY);
};

export const getAiUsage = (): AiUsageEntry[] => {
    try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    } catch (e) {
        return [];
    }
};

const USD_TO_EUR = 0.92;
const VAT_RATE = 0.21;

export const calculateAiCosts = (usage: AiUsageEntry[]) => {
    let totalInput = 0;
    let totalOutput = 0;
    let totalCostUsd = 0;

    // Pricing per million tokens (March 2026)
    const PRICING: Record<string, { input: number, output: number }> = {
        // Claude models (active)
        'claude-sonnet-4-20250514': { input: 3.00 / 1000000, output: 15.00 / 1000000 },
        'claude-opus-4-20250514':   { input: 15.00 / 1000000, output: 75.00 / 1000000 },
        'claude-haiku-4-20250514':  { input: 0.80 / 1000000, output: 4.00 / 1000000 },
        // Legacy Gemini models (for historical logs)
        'gemini-1.5-pro':   { input: 3.50 / 1000000, output: 10.50 / 1000000 },
        'gemini-1.5-flash': { input: 0.075 / 1000000, output: 0.30 / 1000000 },
        'gemini-2.5-pro':   { input: 4.00 / 1000000, output: 12.00 / 1000000 },
        'gemini-2.5-flash': { input: 0.10 / 1000000, output: 0.40 / 1000000 },
        'default':          { input: 3.00 / 1000000, output: 15.00 / 1000000 }
    };

    usage.forEach(entry => {
        totalInput += entry.inputTokens;
        totalOutput += entry.outputTokens;

        const price = PRICING[entry.model] || PRICING['default'];
        totalCostUsd += (entry.inputTokens * price.input) + (entry.outputTokens * price.output);
    });

    const totalCostEur = totalCostUsd * USD_TO_EUR;
    const totalVatEur = totalCostEur * VAT_RATE;
    const totalWithVatEur = totalCostEur + totalVatEur;

    return {
        totalInput,
        totalOutput,
        totalCostUsd,
        totalCostEur,
        totalVatEur,
        totalWithVatEur,
        usdToEur: USD_TO_EUR,
        vatRate: VAT_RATE
    };
};

export const formatUsageSummary = (providedUsage?: AiUsageEntry[]): string => {
    const usage = providedUsage || getAiUsage();
    const stats = calculateAiCosts(usage);

    let text = "AI ENGINE USAGE REPORT\n";
    text += "========================================\n";
    text += `TOTAL REQUESTS: ${usage.length}\n`;
    text += `TOTAL INPUT TOKENS: ${stats.totalInput.toLocaleString()}\n`;
    text += `TOTAL OUTPUT TOKENS: ${stats.totalOutput.toLocaleString()}\n`;
    text += `ESTIMATED COST (USD): $${stats.totalCostUsd.toFixed(4)}\n`;
    text += "========================================\n\n";

    // Summarize per model
    const perModel: Record<string, { count: number, input: number, output: number }> = {};
    usage.forEach(u => {
        if (!perModel[u.model]) perModel[u.model] = { count: 0, input: 0, output: 0 };
        perModel[u.model].count++;
        perModel[u.model].input += u.inputTokens;
        perModel[u.model].output += u.outputTokens;
    });

    Object.entries(perModel).forEach(([model, s]) => {
        text += `MODEL: ${model}\n`;
        text += `  Requests: ${s.count}\n`;
        text += `  Input: ${s.input.toLocaleString()}\n`;
        text += `  Output: ${s.output.toLocaleString()}\n\n`;
    });

    text += "TRACEABILITY:\n";
    const perUserIp: Record<string, { calls: number, costEur: number }> = {};
    usage.forEach(u => {
        const key = `${u.userName || 'Unknown'} (${u.ipAddress || 'Unknown'})`;
        if (!perUserIp[key]) perUserIp[key] = { calls: 0, costEur: 0 };

        // Use a local copy of PRICING or pull from stats if possible. 
        // For simplicity here, we'll use a local minimal version or just assume we have it.
        // Actually, it's better to refactor pricing into a constant.
        const PRICING: Record<string, { input: number, output: number }> = {
            'claude-sonnet-4-20250514': { input: 3.00 / 1000000, output: 15.00 / 1000000 },
            'claude-opus-4-20250514':   { input: 15.00 / 1000000, output: 75.00 / 1000000 },
            'claude-haiku-4-20250514':  { input: 0.80 / 1000000, output: 4.00 / 1000000 },
            'gemini-1.5-pro':   { input: 3.50 / 1000000, output: 10.50 / 1000000 },
            'gemini-1.5-flash': { input: 0.075 / 1000000, output: 0.30 / 1000000 },
            'gemini-2.5-pro':   { input: 4.00 / 1000000, output: 12.00 / 1000000 },
            'gemini-2.5-flash': { input: 0.10 / 1000000, output: 0.40 / 1000000 },
            'default':          { input: 3.00 / 1000000, output: 15.00 / 1000000 }
        };

        const p = PRICING[u.model] || PRICING['default'];
        perUserIp[key].calls++;
        const costUsd = (u.inputTokens * p.input) + (u.outputTokens * p.output);
        perUserIp[key].costEur += costUsd * USD_TO_EUR;
    });

    Object.entries(perUserIp).forEach(([person, s]) => {
        text += `  ${person}: ${s.calls} calls, €${s.costEur.toFixed(4)}\n`;
    });

    return text;
};
